import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useIsFetching,
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef, useState } from "react";

import {
  updateSettings,
  type PlatformSettingsUpdate,
  type ValidatedPlatformSettings,
} from "../api";
import {
  reconcilePlatformSettings,
  retryPlatformSettingsRead,
  SettingsAuthorityError,
  PLATFORM_SETTINGS_MUTATION_KEY,
  PLATFORM_SETTINGS_QUERY_KEY,
} from "../settingsAuthority";
import {
  useSettingsAuthority,
  type SettingsDesiredMutation,
  type SettingsWriteLease,
} from "../settingsAuthorityStore";
import { settingsUpdateErrorMessage } from "../settingsErrors";

function describeUpdateError(error: unknown): string {
  return settingsUpdateErrorMessage(
    error,
    "Failed to update maintenance mode.",
  );
}

const RECONCILIATION_ERROR =
  "The maintenance change could not be confirmed. Retry to refresh settings.";

export function MaintenancePanel({
  settings,
}: {
  settings: ValidatedPlatformSettings;
}) {
  const queryClient = useQueryClient();
  const authority = useSettingsAuthority(queryClient);
  useIsFetching({ exact: true, queryKey: PLATFORM_SETTINGS_QUERY_KEY });
  const settingsWriteCount = useIsMutating({
    exact: true,
    mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
  });
  const initialResolutionSequence = useRef(authority.resolution?.sequence ?? 0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const authorityRecovery =
    authority.recovery?.kind === "maintenance" ? authority.recovery : null;
  const authorityResolution =
    authority.resolution &&
    authority.resolution.sequence > initialResolutionSequence.current &&
    authority.resolution.desired.kind === "maintenance"
      ? authority.resolution
      : null;
  const authorityResolutionDesired =
    authorityResolution?.desired.kind === "maintenance"
      ? authorityResolution.desired
      : null;
  const settingsReadUncertain =
    queryClient.getQueryState(PLATFORM_SETTINGS_QUERY_KEY)?.status === "error";
  const writesBlocked =
    authority.recovery !== null ||
    authority.refreshing ||
    authority.writing ||
    settingsWriteCount > 0 ||
    settingsReadUncertain;
  const displayedUpdateError = authorityRecovery
    ? RECONCILIATION_ERROR
    : authorityResolution?.outcome === "diverged"
      ? "The saved maintenance state differs from this request. Review it before trying again."
      : updateError;
  const dialogOpen =
    confirmationOpen ||
    authorityRecovery !== null ||
    authorityResolution?.outcome === "diverged";

  const mutation = useMutation({
    mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
    mutationFn: async ({
      lease,
      payload,
    }: {
      lease: SettingsWriteLease;
      payload: PlatformSettingsUpdate;
    }) => {
      await updateSettings(payload);
      if (!authority.isWriteOperationCurrent(lease)) return null;
      return reconcilePlatformSettings(queryClient, () =>
        authority.isWriteOperationCurrent(lease),
      );
    },
  });

  const desiredValue = authorityRecovery
    ? authorityRecovery.desiredValue
    : authorityResolution?.outcome === "diverged"
      ? (authorityResolutionDesired?.desiredValue ?? !settings.maintenance_mode)
      : !settings.maintenance_mode;
  const action = desiredValue ? "Enable" : "Disable";

  function openConfirmation() {
    if (mutation.isPending || writesBlocked) return;
    setConfirmationOpen(true);
    setUpdateError(null);
  }

  function closeConfirmation() {
    if (mutation.isPending || writesBlocked) {
      return;
    }
    if (authorityResolution?.outcome === "diverged") {
      authority.clearResolution(authorityResolution);
    }
    setConfirmationOpen(false);
    setUpdateError(null);
  }

  async function confirmUpdate() {
    if (
      mutation.isPending ||
      queryClient.isMutating({
        exact: true,
        mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
      }) > 0 ||
      !authority.tryStartWrite()
    ) {
      return;
    }
    const lease = authority.beginWriteOperation();
    if (!lease) {
      authority.finishWrite();
      return;
    }
    setUpdateError(null);
    const desired = Object.freeze({
      desiredValue,
      kind: "maintenance",
    }) satisfies SettingsDesiredMutation;
    const payload = Object.freeze({
      maintenance_mode: desiredValue,
    }) satisfies PlatformSettingsUpdate;
    try {
      const authoritative = await mutation.mutateAsync({ lease, payload });
      if (!authoritative || !authority.isWriteOperationCurrent(lease)) {
        return;
      }
      if (authoritative.maintenance_mode === desiredValue) {
        authority.recordResolution(desired, "confirmed");
        setConfirmationOpen(false);
      } else {
        authority.recordResolution(desired, "diverged");
      }
    } catch (error) {
      if (!authority.isWriteOperationCurrent(lease)) return;
      if (error instanceof SettingsAuthorityError) {
        authority.recordRecovery(desired);
      } else {
        setUpdateError(describeUpdateError(error));
      }
    } finally {
      authority.finishWrite(lease);
    }
  }

  async function retryAuthorityRead() {
    if (!authorityRecovery || mutation.isPending) return;
    const recovery = authorityRecovery;
    if (!authority.tryStartRefresh(recovery)) return;
    try {
      const authoritative = await retryPlatformSettingsRead(queryClient);
      const confirmed =
        authoritative.maintenance_mode === recovery.desiredValue;
      authority.resolveRecovery(recovery, confirmed ? "confirmed" : "diverged");
      if (confirmed) {
        setConfirmationOpen(false);
        setUpdateError(null);
      } else {
        setConfirmationOpen(true);
      }
    } catch {
      authority.finishRefresh(recovery);
    }
  }

  return (
    <>
      <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Stack spacing={0.5}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
              Maintenance mode
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {settings.maintenance_mode
                ? "Platform access is currently restricted."
                : "Platform access is operating normally."}
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            color={settings.maintenance_mode ? "error" : "inherit"}
            disabled={mutation.isPending || writesBlocked}
            aria-label={`${action} maintenance mode`}
            onClick={openConfirmation}
          >
            {settings.maintenance_mode ? "Active" : "Inactive"}
          </Button>
        </Stack>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={closeConfirmation}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{action} maintenance mode?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This setting affects platform access for every user.
          </DialogContentText>
          {displayedUpdateError ? (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={
                authorityRecovery ? (
                  <Button
                    color="inherit"
                    size="small"
                    disabled={authority.refreshing}
                    onClick={() => void retryAuthorityRead()}
                  >
                    {authority.refreshing ? "Retrying…" : "Retry"}
                  </Button>
                ) : undefined
              }
            >
              {displayedUpdateError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            disabled={mutation.isPending || writesBlocked}
            onClick={closeConfirmation}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color={settings.maintenance_mode ? "error" : "primary"}
            disabled={mutation.isPending || writesBlocked}
            onClick={() => void confirmUpdate()}
          >
            {mutation.isPending ? "Saving…" : action}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
